/**
 * @fileoverview Regression Pack Optimizer API Service
 * @description API calls for the Regression Test Suite Optimizer feature.
 *              Uses the shared apiClient for base URL and auth token.
 * @module features/RegressionOptimizer/services/regressionOptimizerApi
 */

import { apiClient } from '@/services';

const BASE_PATH = '/v1/regression-optimizer';

/**
 * Build the full URL for an endpoint.
 * In dev, uses relative path (Vite proxy handles /api → localhost:5000).
 * In prod, uses apiClient.baseURL.
 */
function buildUrl(path, params) {
  const base = apiClient.baseURL || '/api';
  const url = new URL(`${base}${BASE_PATH}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') url.searchParams.append(k, v);
    });
  }
  return url.toString();
}

/**
 * Get auth headers from the shared apiClient (uses in-memory token from AuthContext).
 * For FormData uploads, do NOT include Content-Type (browser sets multipart boundary).
 */
function getAuthHeaders(includeContentType) {
  const headers = {};
  const token = apiClient._getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (includeContentType) headers['Content-Type'] = 'application/json';
  return headers;
}

/**
 * Extract a human-readable error message from the backend response body.
 * Handles KM Portal format: { success: false, error: { message: "...", error_code: "..." } }
 * Also handles simple: { error: "...", detail: "..." }
 */
function extractErrorMessage(body, fallback) {
  if (!body) return fallback;
  // KM Portal nested: { error: { message: "..." } }
  if (body.error && typeof body.error === 'object') return body.error.message || body.error.error_code || fallback;
  // Simple string
  if (typeof body.error === 'string') return body.error;
  if (typeof body.detail === 'string') return body.detail;
  if (typeof body.message === 'string') return body.message;
  return fallback;
}

export const regressionOptimizerApi = {
  /**
   * Health check — returns { status, llm_provider, llm_model }
   */
  health: async () => {
    const res = await fetch(buildUrl('/health'), { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('API Unreachable');
    return res.json();
  },

  /**
   * Upload Excel file and get back optimized .xlsx as a blob (quick download path).
   */
  optimize: async (file, options = {}) => {
    const { sheet, embeddingThreshold = 0.96, noAi = false, noEmbeddings = false, noLlm = false, signal } = options;
    const params = { embedding_threshold: embeddingThreshold, no_ai: noAi, no_embeddings: noEmbeddings, no_llm: noLlm };
    if (sheet) params.sheet = sheet;
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(buildUrl('/optimize', params), {
      method: 'POST',
      body: formData,
      headers: getAuthHeaders(),
      signal,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `Optimization failed (HTTP ${res.status})`));
    }
    return res.blob();
  },

  /**
   * Upload Excel and get JSON summary + session_id for the review workflow.
   */
  optimizeSummary: async (file, options = {}) => {
    const { sheet, embeddingThreshold = 0.96, temperature = 0.0, noAi = false, noEmbeddings = false, noLlm = false, skipTraining = false, signal } = options;
    const params = { embedding_threshold: embeddingThreshold, temperature, no_ai: noAi, no_embeddings: noEmbeddings, no_llm: noLlm };
    if (sheet) params.sheet = sheet;
    if (skipTraining) params.skip_training = true;
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(buildUrl('/optimize/summary', params), {
      method: 'POST',
      body: formData,
      headers: getAuthHeaders(),
      signal,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `Summary request failed (HTTP ${res.status})`));
    }
    return res.json();
  },

  /**
   * Apply user review decisions and download the final optimized .xlsx.
   */
  finalize: async ({
    sessionId,
    acceptedRemovals = [],
    rejectedRemovals = [],
    acceptedRecommendations = [],
    rejectedRecommendations = [],
    outputFormat = 'bdd',
    userFeedbackHtml = '',
    userFeedbackText = '',
  }) => {
    const res = await fetch(buildUrl('/optimize/finalize'), {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({
        session_id: sessionId,
        accepted_removals: acceptedRemovals,
        rejected_removals: rejectedRemovals,
        accepted_recommendations: acceptedRecommendations,
        rejected_recommendations: rejectedRecommendations,
        output_format: outputFormat,
        user_feedback_html: userFeedbackHtml,
        user_feedback_text: userFeedbackText,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `Finalization failed (HTTP ${res.status})`));
    }
    return res.blob();
  },

  /**
   * Import test cases from an Azure DevOps test suite URL.
   * Returns the test cases as an Excel Blob (to feed into optimize flow).
   */
  importFromAdo: async (urlOrParams) => {
    // Accepts either a URL string or an object { project, planId, suiteId }
    const bodyObj = typeof urlOrParams === 'string'
      ? { url: urlOrParams }
      : { project: urlOrParams.project, plan_id: urlOrParams.planId, suite_id: urlOrParams.suiteId };

    const res = await fetch(buildUrl('/import/ado'), {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify(bodyObj),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `ADO import failed (HTTP ${res.status})`));
    }
    // Extract suite metadata from response headers
    const suiteName = res.headers.get('X-ADO-Suite-Name') || '';
    const suiteId = res.headers.get('X-ADO-Suite-Id') || '';
    const project = res.headers.get('X-ADO-Project') || '';
    const planId = res.headers.get('X-ADO-Plan-Id') || '';
    const path = res.headers.get('X-ADO-Path') || suiteName;
    const pathIdsRaw = res.headers.get('X-ADO-Path-Ids') || '';
    const pathIds = pathIdsRaw ? pathIdsRaw.split(',') : [];
    const parentFolder = res.headers.get('X-ADO-Parent-Folder') || null;
    const depth = parseInt(res.headers.get('X-ADO-Depth') || '0', 10);

    const blob = await res.blob();
    // Extract filename from Content-Disposition or use default
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";\s]+)"?/);
    const filename = match ? match[1] : 'ADO_TestSuite.xlsx';
    const file = new File([blob], filename, { type: blob.type });

    return { file, meta: { suiteName, suiteId, project, planId, path, pathIds, parentFolder, depth } };
  },

  /**
   * Import test cases from an ADO parent suite and all its child suites.
   * Returns { parentName, suites: [{ file, meta }], errors: [] }
   */
  importFromAdoTree: async (urlOrParams) => {
    const bodyObj = typeof urlOrParams === 'string'
      ? { url: urlOrParams }
      : { project: urlOrParams.project, plan_id: urlOrParams.planId, suite_id: urlOrParams.suiteId };

    const res = await fetch(buildUrl('/import/ado/tree'), {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify(bodyObj),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `ADO tree import failed (HTTP ${res.status})`));
    }
    const data = await res.json();

    // Convert each base64 file into a File object
    const suites = (data.suites || []).map((s, idx) => {
      const bytes = Uint8Array.from(atob(s.file_base64), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const file = new File([blob], s.filename, { type: blob.type });
      return {
        file,
        meta: {
          suiteName: s.suite_name,
          suiteId: s.suite_id,
          project: s.project,
          planId: s.plan_id,
          path: s.path || s.suite_name,
          pathIds: s.path_ids || [],
          parentFolder: s.parent_folder || null,
          depth: s.depth ?? 0,
          dfsIndex: s.dfs_index ?? idx,
        },
        testCaseCount: s.test_case_count,
      };
    });

    return {
      parentName: data.parent_suite_name,
      parentSuiteId: data.parent_suite_id,
      suites,
      errors: data.errors || [],
      totalTestCases: data.total_test_cases,
      warning: data.warning || null,
    };
  },

  /**
   * Parse an Excel file and return all rows as JSON (for the diff view).
   */
  parse: async (file, options = {}) => {
    const { sheet, signal } = options;
    const params = {};
    if (sheet) params.sheet = sheet;
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(buildUrl('/parse', params), {
      method: 'POST',
      body: formData,
      headers: getAuthHeaders(),
      signal,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `Parse failed (HTTP ${res.status})`));
    }
    return res.json();
  },

  /**
   * Merge multiple Excel files into a single file.
   * Returns a File object ready for optimization.
   */
  mergeFiles: async (files, options = {}) => {
    const { sheet, suiteLabels } = options;
    const params = {};
    if (sheet) params.sheet = sheet;
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    if (suiteLabels && suiteLabels.length) {
      suiteLabels.forEach(l => formData.append('suiteLabels', l));
    }
    const res = await fetch(buildUrl('/merge', params), {
      method: 'POST',
      body: formData,
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `Merge failed (HTTP ${res.status})`));
    }
    const blob = await res.blob();
    return new File([blob], 'merged_test_suites.xlsx', { type: blob.type });
  },

  /**
   * List all domain agents and their training material status.
   */
  listAgents: async () => {
    const res = await fetch(buildUrl('/agents'), { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Failed to fetch agents');
    return res.json();
  },

  /**
   * Upload training material (PDF/DOCX/TXT) for a domain agent.
   */
  uploadTraining: async (domain, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(buildUrl(`/agents/${domain}/training`), {
      method: 'POST',
      body: formData,
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `Training upload failed (HTTP ${res.status})`));
    }
    return res.json();
  },

  /**
   * Classify an uploaded Excel file into a domain agent (without running optimization).
   */
  classify: async (file, options = {}) => {
    const { sheet, signal } = options;
    const params = {};
    if (sheet) params.sheet = sheet;
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(buildUrl('/classify', params), {
      method: 'POST',
      body: formData,
      headers: getAuthHeaders(),
      signal,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `Classification failed (HTTP ${res.status})`));
    }
    return res.json();
  },

  // ── Scheduled Optimization ──

  /**
   * Submit a new scheduled optimization job (file upload).
   */
  scheduleJobFile: async (file, config = {}) => {
    const params = {};
    if (config.threshold != null) params.embedding_threshold = config.threshold;
    if (config.temperature != null) params.temperature = config.temperature;
    if (config.sheet) params.sheet = config.sheet;
    if (config.domainAgent) params.domain_agent = config.domainAgent;
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(buildUrl('/schedule', params), {
      method: 'POST',
      body: formData,
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `Schedule failed (HTTP ${res.status})`));
    }
    return res.json();
  },

  /**
   * Submit a new scheduled optimization job (ADO URL).
   */
  scheduleJobAdo: async (url, config = {}) => {
    const params = {};
    if (config.threshold != null) params.embedding_threshold = config.threshold;
    if (config.temperature != null) params.temperature = config.temperature;
    if (config.sheet) params.sheet = config.sheet;
    if (config.domainAgent) params.domain_agent = config.domainAgent;
    const res = await fetch(buildUrl('/schedule', params), {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ url }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `Schedule failed (HTTP ${res.status})`));
    }
    return res.json();
  },

  /**
   * List all scheduled jobs for the current user.
   */
  listScheduledJobs: async () => {
    const res = await fetch(buildUrl('/schedule/jobs'), {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Failed to fetch scheduled jobs');
    return res.json();
  },

  /**
   * Get a single scheduled job by ID (includes result_data if completed).
   */
  getScheduledJob: async (jobId) => {
    const res = await fetch(buildUrl(`/schedule/jobs/${jobId}`), {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Failed to fetch job details');
    return res.json();
  },

  /**
   * Delete/cancel a scheduled job.
   */
  deleteScheduledJob: async (jobId) => {
    const res = await fetch(buildUrl(`/schedule/jobs/${jobId}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(extractErrorMessage(err, `Delete failed (HTTP ${res.status})`));
    }
    return res.json();
  },
};
